import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Box, IconButton, Tooltip, useTheme } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import mermaid from 'mermaid';

function initMermaid(mode) {
  mermaid.initialize({
    startOnLoad: false,
    theme: mode === 'dark' ? 'dark' : 'neutral',
    securityLevel: 'strict',
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  });
}
initMermaid('light');

let mermaidIdCounter = 0;

const codeBlockChrome = {
  bodyBg: '#111318',
};

const cleanedOneDark = {
  ...oneDark,
  'pre[class*="language-"]': {
    ...oneDark['pre[class*="language-"]'],
    background: codeBlockChrome.bodyBg,
    textShadow: 'none',
    fontFamily: '"SF Mono", SFMono-Regular, Consolas, "Liberation Mono", Menlo, monospace',
  },
  'code[class*="language-"]': {
    ...oneDark['code[class*="language-"]'],
    background: 'transparent',
    textShadow: 'none',
    fontFamily: '"SF Mono", SFMono-Regular, Consolas, "Liberation Mono", Menlo, monospace',
  },
};

function cleanupMermaidRender(id) {
  for (const sel of [`#${id}`, `#d${id}`, `[data-id="${id}"]`]) {
    try { document.querySelector(sel)?.remove(); } catch { /* ignore */ }
  }
}

export function CopyBtn({ text, light }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [text]);

  return (
    <Tooltip title={copied ? 'Copied!' : 'Copy'} placement="top" arrow>
      <IconButton
        onClick={handleCopy}
        size="small"
        sx={{
          width: 26,
          height: 26,
          borderRadius: '6px',
          color: copied ? '#34d399' : (light ? '#a1a1aa' : 'rgba(255,255,255,0.4)'),
          transition: 'all 150ms ease',
          '&:hover': {
            backgroundColor: light ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.08)',
            color: copied ? '#34d399' : (light ? '#3f3f46' : 'rgba(255,255,255,0.8)'),
          },
        }}
      >
        {copied
          ? <CheckIcon sx={{ fontSize: 14 }} />
          : <ContentCopyIcon sx={{ fontSize: 14 }} />
        }
      </IconButton>
    </Tooltip>
  );
}

/**
 * Renders a Mermaid diagram with optional diagram/code toggle.
 * @param {{ chart: string, variant?: 'full' | 'preview' }} props
 */
export function MermaidBlock({ chart, variant = 'full' }) {
  const theme = useTheme();
  const mode = theme.palette.mode;
  const containerRef = useRef(null);
  const [svg, setSvg] = useState('');
  const [failed, setFailed] = useState(false);
  const [view, setView] = useState('diagram');
  const previewOnly = variant === 'preview';

  useEffect(() => {
    if (!chart) return;
    let cancelled = false;
    const id = `mmd${mermaidIdCounter++}`;

    const sandbox = document.createElement('div');
    sandbox.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:0;height:0;overflow:hidden;visibility:hidden;';
    document.body.appendChild(sandbox);

    (async () => {
      try {
        initMermaid(mode);
        const { svg: renderedSvg } = await mermaid.render(id, chart, sandbox);
        if (!cancelled) setSvg(renderedSvg);
      } catch {
        if (!cancelled) setFailed(true);
      } finally {
        sandbox.remove();
        cleanupMermaidRender(id);
      }
    })();

    return () => {
      cancelled = true;
      sandbox.remove();
      cleanupMermaidRender(id);
    };
  }, [chart, mode]);

  const showCode = failed || view === 'code';
  const canToggle = !previewOnly && !failed && svg;
  const isDark = mode === 'dark';

  return (
    <Box
      sx={{
        my: previewOnly ? 0 : 1.5,
        borderRadius: '10px',
        overflow: 'hidden',
        border: 1,
        borderColor: 'divider',
        backgroundColor: showCode
          ? '#1e1e1e'
          : (isDark ? 'background.paper' : '#fff'),
        transition: 'background-color 200ms ease',
      }}
    >
      {!previewOnly && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.25,
            height: 36,
            backgroundColor: showCode
              ? '#282828'
              : (isDark ? 'background.subtle' : '#fafafa'),
            borderBottom: 1,
            borderColor: showCode ? '#333' : 'divider',
            transition: 'all 200ms ease',
          }}
        >
          <Box
            component="span"
            sx={{
              fontSize: '11px',
              fontWeight: 600,
              color: showCode ? 'rgba(255,255,255,0.45)' : 'text.secondary',
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              mr: 'auto',
            }}
          >
            Mermaid
          </Box>

          {canToggle && (
            <Box
              sx={{
                display: 'flex',
                height: 24,
                borderRadius: '6px',
                backgroundColor: showCode
                  ? 'rgba(255,255,255,0.06)'
                  : (isDark ? 'action.hover' : '#f0f0f0'),
                p: '2px',
                transition: 'background-color 200ms ease',
              }}
            >
              {['diagram', 'code'].map((v) => (
                <Box
                  key={v}
                  onClick={() => setView(v)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    px: 1,
                    borderRadius: '5px',
                    fontSize: '11px',
                    fontWeight: 500,
                    cursor: 'pointer',
                    userSelect: 'none',
                    transition: 'all 150ms ease',
                    color: view === v
                      ? (showCode ? '#fff' : 'text.primary')
                      : (showCode ? 'rgba(255,255,255,0.4)' : 'text.disabled'),
                    backgroundColor: view === v
                      ? (showCode
                          ? 'rgba(255,255,255,0.1)'
                          : (isDark ? 'background.paper' : '#fff'))
                      : 'transparent',
                    boxShadow: view === v ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
                    '&:hover': {
                      color: view === v
                        ? (showCode ? '#fff' : 'text.primary')
                        : (showCode ? 'rgba(255,255,255,0.65)' : 'text.secondary'),
                    },
                  }}
                >
                  {v === 'diagram' ? 'Preview' : 'Code'}
                </Box>
              ))}
            </Box>
          )}

          <CopyBtn text={chart} light={!showCode && !isDark} />
        </Box>
      )}

      {showCode && !previewOnly ? (
        <SyntaxHighlighter
          style={cleanedOneDark}
          language="text"
          PreTag="div"
          wrapLongLines
          customStyle={{
            margin: 0,
            borderRadius: 0,
            fontSize: '12.5px',
            lineHeight: 1.65,
            padding: '14px 16px',
            background: codeBlockChrome.bodyBg,
            overflowX: 'auto',
          }}
        >
          {chart}
        </SyntaxHighlighter>
      ) : svg ? (
        <Box
          ref={containerRef}
          sx={{
            p: previewOnly ? 1.5 : 2.5,
            display: 'flex',
            justifyContent: 'center',
            overflowX: 'auto',
            '& svg': { maxWidth: '100%', height: 'auto', display: 'block' },
          }}
          dangerouslySetInnerHTML={{ __html: svg }}
        />
      ) : failed && previewOnly ? (
        <Box sx={{ p: 1.5, fontSize: '12px', color: 'error.main' }}>
          Invalid Mermaid syntax
        </Box>
      ) : null}
    </Box>
  );
}
